import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthenticatedHeader from '../../components/ui/AuthenticatedHeader';
import NavigationBreadcrumbs from '../../components/ui/NavigationBreadcrumbs';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import FilterControls from './components/FilterControls';
import PartnerTableRow from './components/PartnerTableRow';
import PartnerCard from './components/PartnerCard';
import BulkActionsBar from './components/BulkActionsBar';
import EditPartnerModal from './components/EditPartnerModal';
import AddPartnerModal from './components/AddPartnerModal';
import { useAuth } from '../../contexts/AuthContext';

import { supabase } from '../../lib/supabase';

const AdminPartnerManagement = () => {
  const navigate = useNavigate();
  const { user, userProfile, loading: authLoading, profileLoading } = useAuth();
  const [partners, setPartners] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [industryFilter, setIndustryFilter] = useState('all');
  const [leadVolumeFilter, setLeadVolumeFilter] = useState('all');
  const [dateRangeStart, setDateRangeStart] = useState('');
  const [dateRangeEnd, setDateRangeEnd] = useState('');
  const [selectedPartners, setSelectedPartners] = useState([]);
  const [editingPartner, setEditingPartner] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);

  useEffect(() => {
    const checkMobileView = () => {
      setIsMobileView(window.innerWidth < 1024);
    };
    
    checkMobileView();
    window.addEventListener('resize', checkMobileView);
    
    return () => window.removeEventListener('resize', checkMobileView);
  }, []);

  useEffect(() => {
    const fetchPartners = async () => {
      try {
        // Wait for both auth and profile to load completely
        if (authLoading || profileLoading) {
          return;
        }

        // Check if user is admin using userProfile (not user)
        if (userProfile?.role !== 'admin') {
          console.warn('Access denied: User is not an admin');
          navigate('/partner-dashboard');
          return;
        }

        setIsLoading(true);
        
        // Fetch all partners
        const { data: partnersData, error: partnersError } = await supabase
          ?.from('user_profiles')
          ?.select('*')
          ?.eq('role', 'partner')
          ?.order('created_at', { ascending: false });

        if (partnersError) {
          console.error('Error fetching partners:', partnersError);
          return;
        }

        // Fetch all leads to calculate statistics
        const { data: leadsData, error: leadsError } = await supabase
          ?.from('leads')
          ?.select('partner_id, status');

        if (leadsError) {
          console.error('Error fetching leads:', leadsError);
        }

        // Create a map of lead statistics by partner_id
        const leadStats = {};
        leadsData?.forEach(lead => {
          const partnerId = lead?.partner_id;
          if (!leadStats?.[partnerId]) {
            leadStats[partnerId] = {
              total: 0,
              converted: 0
            };
          }
          leadStats[partnerId].total++;
          
          // Count as converted if status is 'Closed Won' or 'Complete'
          if (lead?.status === 'Closed Won' || lead?.status === 'Complete') {
            leadStats[partnerId].converted++;
          }
        });

        // Transform database data to match UI format
        const transformedPartners = partnersData?.map(partner => {
          const stats = leadStats?.[partner?.id] || { total: 0, converted: 0 };
          
          // Use updated_at as a proxy for last activity
          const lastActivity = partner?.updated_at 
            ? new Date(partner?.updated_at)?.toLocaleDateString('en-US', { 
                month: '2-digit', 
                day: '2-digit', 
                year: 'numeric' 
              })
            : 'Never';
          
          return {
            id: partner?.id,
            companyName: partner?.company_name,
            contactName: partner?.full_name,
            email: partner?.email,
            phone: partner?.phone,
            industry: partner?.industry_type || 'other',
            status: partner?.partner_status,
            totalLeads: stats?.total,
            convertedLeads: stats?.converted,
            registrationDate: new Date(partner?.created_at)?.toLocaleDateString('en-US', { 
              month: '2-digit', 
              day: '2-digit', 
              year: 'numeric' 
            }),
            lastActivity: lastActivity,
            salesforceId: partner?.salesforce_account_id,
            taxId: partner?.tax_id,
            address: partner?.business_address,
            city: '', // Extract from business_address if needed
            state: '', // Extract from business_address if needed
            zipCode: '', // Extract from business_address if needed
            website: partner?.website,
            notes: '', // Add notes field to database if needed
            emailVerified: true, // TODO: Check auth.users table
            twoFactorEnabled: false, // TODO: Check auth settings
            cesspoolConversionRewardAmount: Number(partner?.cesspool_conversion_reward_amount) || 500,
            otherReferralRewardAmount: Number(partner?.other_referral_reward_amount) || 50,
          };
        });

        setPartners(transformedPartners);
      } catch (error) {
        console.error('Error loading partners:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPartners();
  }, [userProfile, authLoading, profileLoading, navigate]);

  const filteredPartners = partners?.filter(partner => {
    const matchesSearch = 
      partner?.companyName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      partner?.contactName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      partner?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      partner?.salesforceId?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || partner?.status === statusFilter;
    const matchesIndustry = industryFilter === 'all' || partner?.industry === industryFilter;
    
    const matchesLeadVolume = (() => {
      if (leadVolumeFilter === 'all') return true;
      if (leadVolumeFilter === '0-10') return partner?.totalLeads <= 10;
      if (leadVolumeFilter === '11-50') return partner?.totalLeads >= 11 && partner?.totalLeads <= 50;
      if (leadVolumeFilter === '51-100') return partner?.totalLeads >= 51 && partner?.totalLeads <= 100;
      if (leadVolumeFilter === '100+') return partner?.totalLeads > 100;
      return true;
    })();

    const matchesDateRange = (() => {
      if (!dateRangeStart && !dateRangeEnd) return true;
      const partnerDate = new Date(partner.registrationDate);
      const startDate = dateRangeStart ? new Date(dateRangeStart) : null;
      const endDate = dateRangeEnd ? new Date(dateRangeEnd) : null;
      
      if (startDate && endDate) {
        return partnerDate >= startDate && partnerDate <= endDate;
      } else if (startDate) {
        return partnerDate >= startDate;
      } else if (endDate) {
        return partnerDate <= endDate;
      }
      return true;
    })();

    return matchesSearch && matchesStatus && matchesIndustry && matchesLeadVolume && matchesDateRange;
  });

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setIndustryFilter('all');
    setLeadVolumeFilter('all');
    setDateRangeStart('');
    setDateRangeEnd('');
  };

  const handleSelectPartner = (partnerId, isSelected) => {
    setSelectedPartners(prev => 
      isSelected 
        ? [...prev, partnerId]
        : prev?.filter(id => id !== partnerId)
    );
  };

  const handleSelectAll = (e) => {
    if (e?.target?.checked) {
      setSelectedPartners(filteredPartners?.map(p => p?.id));
    } else {
      setSelectedPartners([]);
    }
  };

  const handleDeselectAll = () => {
    setSelectedPartners([]);
  };

  const handleEditPartner = (partner) => {
    setEditingPartner(partner);
    setIsEditModalOpen(true);
  };

  const handleSavePartner = async (updatedPartner) => {
    try {
      // Update partner profile in Supabase
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.update({
          full_name: updatedPartner?.contactName,
          company_name: updatedPartner?.companyName,
          phone: updatedPartner?.phone,
          industry_type: updatedPartner?.industry,
          partner_status: updatedPartner?.status,
          tax_id: updatedPartner?.taxId,
          business_address: updatedPartner?.address,
          website: updatedPartner?.website,
          cesspool_conversion_reward_amount: updatedPartner?.cesspoolConversionRewardAmount,
          other_referral_reward_amount: updatedPartner?.otherReferralRewardAmount,
        })
        ?.eq('id', updatedPartner?.id)
        ?.select()
        ?.single();

      if (error) {
        console.error('Error updating partner:', error);
        alert('Failed to update partner information. Please try again.');
        return;
      }

      // Update local state
      setPartners(prev => 
        prev?.map(p => p?.id === updatedPartner?.id ? updatedPartner : p)
      );

      alert('Partner information updated successfully!');
    } catch (error) {
      console.error('Error saving partner:', error);
      alert('An error occurred while updating partner information.');
    }
  };

  const handleAddPartner = (newPartnerData) => {
    const newPartner = {
      ...newPartnerData,
      id: partners?.length + 1,
      status: 'pending',
      totalLeads: 0,
      convertedLeads: 0,
      registrationDate: new Date()?.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }),
      lastActivity: new Date()?.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }),
      salesforceId: `SF-ACC-${String(partners?.length + 1)?.padStart(6, '0')}`,
      notes: '',
      emailVerified: false,
      twoFactorEnabled: false,
      cesspoolConversionRewardAmount: 0,
      otherReferralRewardAmount: 0,
    };
    setPartners(prev => [...prev, newPartner]);
  };

  const handleViewLeads = (partnerId) => {
    navigate('/lead-details', { state: { partnerId } });
  };

  const handleToggleStatus = (partner) => {
    const newStatus = partner?.status === 'suspended' ? 'verified' : 'suspended';
    setPartners(prev =>
      prev?.map(p => p?.id === partner?.id ? { ...p, status: newStatus } : p)
    );
  };

  const handleViewSalesforce = (salesforceId) => {
    window.open(`https://salesforce.com/account/${salesforceId}`, '_blank');
  };

  const handleBulkVerify = () => {
    setPartners(prev =>
      prev?.map(p => selectedPartners?.includes(p?.id) ? { ...p, status: 'verified' } : p)
    );
    setSelectedPartners([]);
  };

  const handleBulkSuspend = () => {
    setPartners(prev =>
      prev?.map(p => selectedPartners?.includes(p?.id) ? { ...p, status: 'suspended' } : p)
    );
    setSelectedPartners([]);
  };

  const handleBulkEmail = () => {
    alert(`Sending email to ${selectedPartners?.length} selected partner(s)`);
  };

  const handleBulkExport = () => {
    const selectedData = partners?.filter(p => selectedPartners?.includes(p?.id));
    console.log('Exporting partners:', selectedData);
    alert(`Exporting ${selectedPartners?.length} partner(s) to CSV`);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <AuthenticatedHeader />
        <NavigationBreadcrumbs />
        <main className="main-content pt-20 px-6 pb-8">
          <div className="max-w-7xl mx-auto">
            <div className="bg-card rounded-lg p-8 border border-border elevation-1 animate-pulse">
              <div className="h-8 bg-muted rounded w-1/3 mb-8"></div>
              <div className="space-y-4">
                {[1, 2, 3, 4]?.map((i) => (
                  <div key={i} className="h-24 bg-muted rounded"></div>
                ))}
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AuthenticatedHeader />
      <NavigationBreadcrumbs />
      <main className="main-content pt-20 px-6 pb-24">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-semibold text-foreground mb-2">Partner Management</h1>
              <p className="text-muted-foreground">Manage and oversee all partner accounts with comprehensive editing capabilities</p>
            </div>
            <Button
              variant="default"
              iconName="UserPlus"
              iconPosition="left"
              onClick={() => setIsAddModalOpen(true)}
            >
              Add New Partner
            </Button>
          </div>

          <FilterControls
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            statusFilter={statusFilter}
            onStatusChange={setStatusFilter}
            industryFilter={industryFilter}
            onIndustryChange={setIndustryFilter}
            leadVolumeFilter={leadVolumeFilter}
            onLeadVolumeChange={setLeadVolumeFilter}
            dateRangeStart={dateRangeStart}
            onDateRangeStartChange={setDateRangeStart}
            dateRangeEnd={dateRangeEnd}
            onDateRangeEndChange={setDateRangeEnd}
            onClearFilters={handleClearFilters}
            resultCount={filteredPartners?.length}
          />

          {isMobileView ? (
            <div className="grid grid-cols-1 gap-4">
              {filteredPartners?.map((partner) => (
                <PartnerCard
                  key={partner?.id}
                  partner={partner}
                  onEdit={handleEditPartner}
                  onViewLeads={handleViewLeads}
                  onToggleStatus={handleToggleStatus}
                  onViewSalesforce={handleViewSalesforce}
                  isSelected={selectedPartners?.includes(partner?.id)}
                  onSelect={handleSelectPartner}
                />
              ))}
            </div>
          ) : (
            <div className="bg-card rounded-lg border border-border elevation-1 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/30 border-b border-border">
                    <tr>
                      <th className="px-6 py-4 text-left">
                        <input
                          type="checkbox"
                          checked={selectedPartners?.length === filteredPartners?.length && filteredPartners?.length > 0}
                          onChange={handleSelectAll}
                          className="w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2"
                          aria-label="Select all partners"
                        />
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">Company</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">Contact</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">Status</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">Leads</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">Registration</th>
                      <th className="px-6 py-4 text-right text-sm font-semibold text-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filteredPartners?.map((partner) => (
                      <PartnerTableRow
                        key={partner?.id}
                        partner={partner}
                        onEdit={handleEditPartner}
                        onViewLeads={handleViewLeads}
                        onToggleStatus={handleToggleStatus}
                        onViewSalesforce={handleViewSalesforce}
                        isSelected={selectedPartners?.includes(partner?.id)}
                        onSelect={handleSelectPartner}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {filteredPartners?.length === 0 && (
            <div className="bg-card rounded-lg border border-border elevation-1 p-12 text-center">
              <Icon name="Users" size={48} className="mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-semibold text-foreground mb-2">No partners found</h3>
              <p className="text-muted-foreground mb-6">Try adjusting your search or filter criteria</p>
              <Button
                variant="outline"
                iconName="RefreshCw"
                iconPosition="left"
                onClick={handleClearFilters}
              >
                Clear Filters
              </Button>
            </div>
          )}
        </div>
      </main>
      <BulkActionsBar
        selectedCount={selectedPartners?.length}
        onVerifySelected={handleBulkVerify}
        onSuspendSelected={handleBulkSuspend}
        onEmailSelected={handleBulkEmail}
        onExportSelected={handleBulkExport}
        onDeselectAll={handleDeselectAll}
      />
      <EditPartnerModal
        partner={editingPartner}
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingPartner(null);
        }}
        onSave={handleSavePartner}
      />
      <AddPartnerModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onAdd={handleAddPartner}
      />
    </div>
  );
};

export default AdminPartnerManagement;